import { Inject, Injectable, InjectionToken } from '@angular/core';
import { CussService } from '@services/cuss.service';
import { environment } from '@env/environment';
import { METRIC_NAME } from '@app/enums/new-relic.enum';

export const WINDOW = new InjectionToken<Window>('Global window object', {
  factory: () => window,
});

export type ErrorSource =
  | 'GlobalErrorHandler'
  | 'HttpInterceptor'
  | 'UnhandledRejection'
  | 'ServiceCatch'
  | 'ConsoleError';

/**
 * Servicio centralizado para reportar errores de TypeScript/JavaScript a New Relic.
 * Envía cada error como:
 *  - noticeError() → aparece en JS Errors de New Relic
 *  - addPageAction('KIOSK_SSBD', { actionSubname: 'KIOSK_UNHANDLED_ERROR', ... }) → PageAction consultable con NRQL
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorLoggerService {
  private readonly ACTION_NAME = METRIC_NAME.KIOSK_AM; // 'KIOSK_SSBD'
  private readonly ACTION_SUBNAME = 'KIOSK_UNHANDLED_ERROR';

  constructor(
    @Inject(WINDOW) private windowRef: Window & { newrelic?: NewRelic },
    private _cussService: CussService
  ) {}

  /**
   * Reporta un error a New Relic con contexto enriquecido.
   *
   * @param error - El error o excepción a reportar
   * @param source - Origen del error (GlobalErrorHandler, HttpInterceptor, etc.)
   * @param context - Atributos adicionales opcionales (httpUrl, httpStatus, etc.)
   */
  logError(error: any, source: ErrorSource, context?: Record<string, unknown>): void {
    setTimeout(() => {
      try {
        const nr = this.getNewRelicAgent();
        if (!nr?.addPageAction) return;

        const errorObj = error instanceof Error ? error : new Error(String(error));

        const attrs: Record<string, string | number | boolean | null> = {
          name: this.ACTION_NAME,
          actionSubname: this.ACTION_SUBNAME,
          errorSource: source,
          errorType: errorObj.name || 'Unknown',
          errorMessage: (errorObj.message || '').substring(0, 500),
          errorStack: (errorObj.stack || '').substring(0, 1000),
          kioskId: this._cussService?.KioskID || 'unknown',
          pnr: this.getReservationCode(),
          currentRoute: window.location.hash || window.location.pathname,
          environment: environment.production ? 'production' : 'cert',
          isSSBD: true,
          isError: true,
          timestamp: new Date().toISOString(),
          ...this.sanitize(context || {}),
        };

        nr.noticeError(errorObj, attrs);
        nr.addPageAction(this.ACTION_NAME, attrs);
      } catch (e) {
        console.warn('[ErrorLoggerService] Failed to report error to New Relic', e);
      }
    }, 0);
  }

  private getNewRelicAgent(): NewRelic | undefined {
    try {
      return this.windowRef?.newrelic as NewRelic | undefined;
    } catch {
      return undefined;
    }
  }

  private getReservationCode(): string {
    try {
      const data = sessionStorage.getItem('reservation');
      if (data) {
        const reservation = JSON.parse(data);
        return reservation?.pnr || 'empty';
      }
      // eslint-disable-next-line no-empty
    } catch {
      return 'empty';
    }
    return 'empty';
  }

  private sanitize(raw: Record<string, unknown>): Record<string, string | number | boolean | null> {
    const out: Record<string, string | number | boolean | null> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (value === null || value === undefined) {
        out[key] = null;
        continue;
      }
      const t = typeof value;
      if (t === 'string' || t === 'number' || t === 'boolean') {
        out[key] = value as string | number | boolean;
        continue;
      }
      try {
        out[key] = JSON.stringify(value);
      } catch {
        out[key] = String(value);
      }
    }
    return out;
  }
}
